import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useAuth } from '../../auth/useAuth'
import { useAdaptivePolling } from '../../hooks/useAdaptivePolling'
import { fetchAlertsCount } from '../../pages/spot-search/api/spotSearchApi'
import { errorStatus } from '../../pages/spot-search/api/spotSearchErrors'
import type { AlertsCount } from '../../pages/spot-search/model/types'
import { SpotSearchAlertsContext, summarizeAlerts, type SpotSearchAlertsValue } from './useSpotSearchAlerts'

// Licznik w menu (spec §11.5) na wzór PartnerOffersBadgeProvider, ale 404 NIE znaczy „moduł wyłączony”:
// na /api/spot-search/* to backend w trakcie wdrożenia — cicha ponowna próba po 2 s, dalej co 60 s,
// badge ukryty. Inny błąd zostawia poprzednią wartość; po 10 min bez sukcesu badge znika.

export const ALERTS_POLL_MS = 60_000
export const ALERTS_POLL_NO_ROUTES_MS = 5 * 60_000
export const ALERTS_NOT_FOUND_RETRY_MS = 2_000
export const ALERTS_STALE_AFTER_MS = 10 * 60_000

export function SpotSearchAlertsProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  // Licznik związany z tożsamością: wylogowanie to clearAuth bez przeładowania, więc po zalogowaniu B
  // menu nie może pokazać licznika (ani trasy docelowej) A (katalog iso-front-wspolny-komputer).
  const [state, setState] = useState<{ token: string | null; counts: AlertsCount | null }>({ token: null, counts: null })
  const counts = state.token === token ? state.counts : null
  const abortRef = useRef<AbortController | null>(null)
  const lastSuccessAtRef = useRef<number | null>(null)
  const hasRoutesRef = useRef(true)
  const notFoundRetriedRef = useRef(false)
  const retryTimerRef = useRef<number | null>(null)
  const requestRefreshRef = useRef<() => void>(() => undefined)

  const tick = useCallback(async () => {
    if (!token) {
      return
    }
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const next = await fetchAlertsCount({ token, signal: controller.signal })
      if (controller.signal.aborted) {
        return
      }
      lastSuccessAtRef.current = Date.now()
      hasRoutesRef.current = next.routes.length > 0
      notFoundRetriedRef.current = false
      setState({ token, counts: next })
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      if (errorStatus(error) === 404) {
        setState({ token, counts: null })
        if (!notFoundRetriedRef.current) {
          notFoundRetriedRef.current = true
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null
            requestRefreshRef.current()
          }, ALERTS_NOT_FOUND_RETRY_MS)
        }
        return
      }
      const lastSuccessAt = lastSuccessAtRef.current
      if (lastSuccessAt === null || Date.now() - lastSuccessAt >= ALERTS_STALE_AFTER_MS) {
        setState({ token, counts: null })
      }
    }
  }, [token])

  const getDelayMs = useCallback(() => (hasRoutesRef.current ? ALERTS_POLL_MS : ALERTS_POLL_NO_ROUTES_MS), [])
  // Powrót do karty: najwyżej raz na 60 s (lekcja #190 — każdy Alt+Tab to nie GET na każdej stronie portalu).
  const { requestImmediateRefresh } = useAdaptivePolling({
    enabled: Boolean(token),
    getDelayMs,
    refresh: tick,
    minVisibilityRefreshGapMs: ALERTS_POLL_MS,
  })

  useEffect(() => {
    requestRefreshRef.current = requestImmediateRefresh
  }, [requestImmediateRefresh])

  useEffect(() => {
    if (!token) {
      return undefined
    }
    const initialTimer = window.setTimeout(() => requestImmediateRefresh(), 0)
    return () => {
      window.clearTimeout(initialTimer)
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
      abortRef.current?.abort()
      lastSuccessAtRef.current = null
      notFoundRetriedRef.current = false
      hasRoutesRef.current = true
    }
  }, [requestImmediateRefresh, token])

  const value = useMemo<SpotSearchAlertsValue>(
    () => ({ counts, ...summarizeAlerts(counts), refresh: requestImmediateRefresh }),
    [counts, requestImmediateRefresh]
  )

  return <SpotSearchAlertsContext.Provider value={value}>{children}</SpotSearchAlertsContext.Provider>
}
